import * as spine from "@esotericsoftware/spine-webgl";
import { SpinePreviewApp } from "../../../../../lib/SpinePreviewApp";
import {
  captureAllFrames,
  captureStaticStackFramesOnly,
} from "./exportFrames";
import type { StaticStackItem } from "../../../../../lib/staticLayerStack";

export interface ExportWebpOptions {
  app?: SpinePreviewApp | null;
  spineCanvas?: spine.SpineCanvas | null;
  outputSize?: number;
  fps?: number;
  quality?: number;
  durationMs?: number;
  maxFrames?: number;
  transparentBackground?: boolean;
  highQualityCapture?: boolean;
  onProgress?: (percent: number) => void;
  staticStackItems?: StaticStackItem[];
  staticUrlsBelow?: string[];
  staticUrlsAbove?: string[];
  konvaUnderlay?: HTMLCanvasElement | null;
  konvaOverlay?: HTMLCanvasElement | null;
  spineAboveApp?: SpinePreviewApp | null;
  spineAboveCanvas?: spine.SpineCanvas | null;
  spineBelowRender?: boolean;
}

function canvasToBlobAsync(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("canvas.toBlob 返回 null"))),
      type,
      quality,
    );
  });
}

function writeUint16LE(view: DataView, offset: number, value: number) {
  view.setUint16(offset, value, true);
}

function writeUint24LE(view: DataView, offset: number, value: number) {
  view.setUint8(offset, value & 0xff);
  view.setUint8(offset + 1, (value >> 8) & 0xff);
  view.setUint8(offset + 2, (value >> 16) & 0xff);
}

function writeUint32LE(view: DataView, offset: number, value: number) {
  view.setUint32(offset, value, true);
}

function writeFourCC(view: DataView, offset: number, fourcc: string) {
  for (let i = 0; i < 4; i++) {
    view.setUint8(offset + i, fourcc.charCodeAt(i));
  }
}

function readFourCC(view: DataView, offset: number): string {
  return String.fromCharCode(
    view.getUint8(offset), view.getUint8(offset + 1),
    view.getUint8(offset + 2), view.getUint8(offset + 3),
  );
}

async function extractFrameData(blob: Blob): Promise<Uint8Array> {
  const buf = await blob.arrayBuffer();
  const view = new DataView(buf);
  const parts: Uint8Array[] = [];

  let offset = 12;
  while (offset < buf.byteLength) {
    const fourcc = readFourCC(view, offset);
    const chunkDataSize = view.getUint32(offset + 4, true);
    const chunkTotalSize = 8 + chunkDataSize + (chunkDataSize % 2);

    if (fourcc === "ALPH" || fourcc === "VP8 " || fourcc === "VP8L") {
      parts.push(new Uint8Array(buf, offset, chunkTotalSize));
    }

    offset += chunkTotalSize;
  }

  if (parts.length === 0) {
    throw new Error("WebP 中未找到 VP8/VP8L bitstream");
  }

  if (parts.length === 1) return parts[0];

  const totalLen = parts.reduce((s, p) => s + p.byteLength, 0);
  const merged = new Uint8Array(totalLen);
  let pos = 0;
  for (const p of parts) {
    merged.set(p, pos);
    pos += p.byteLength;
  }
  return merged;
}

function muxAnimatedWebp(
  frameChunks: Uint8Array[],
  frameDurations: number[],
  width: number,
  height: number,
): Blob {
  const vp8xPayloadSize = 10;
  const vp8xChunkSize = 8 + vp8xPayloadSize;

  const animPayloadSize = 6;
  const animChunkSize = 8 + animPayloadSize;

  let anmfTotalSize = 0;
  const anmfHeaders: ArrayBuffer[] = [];
  for (let i = 0; i < frameChunks.length; i++) {
    const frameDataSize = frameChunks[i].byteLength;
    const anmfPayloadSize = 16 + frameDataSize;
    const anmfPaddedSize = 8 + anmfPayloadSize + (anmfPayloadSize % 2);
    anmfTotalSize += anmfPaddedSize;

    const headerBuf = new ArrayBuffer(24);
    const hv = new DataView(headerBuf);
    writeFourCC(hv, 0, "ANMF");
    writeUint32LE(hv, 4, anmfPayloadSize);
    writeUint24LE(hv, 8, 0);
    writeUint24LE(hv, 11, 0);
    writeUint24LE(hv, 14, width - 1);
    writeUint24LE(hv, 17, height - 1);
    writeUint24LE(hv, 20, frameDurations[i]);
    hv.setUint8(23, 0x03);
    anmfHeaders.push(headerBuf);
  }

  const riffPayloadSize = 4 + vp8xChunkSize + animChunkSize + anmfTotalSize;

  const headerSize = 12 + vp8xChunkSize + animChunkSize;
  const headerBuf = new ArrayBuffer(headerSize);
  const hv = new DataView(headerBuf);
  let off = 0;

  writeFourCC(hv, off, "RIFF"); off += 4;
  writeUint32LE(hv, off, riffPayloadSize); off += 4;
  writeFourCC(hv, off, "WEBP"); off += 4;

  writeFourCC(hv, off, "VP8X"); off += 4;
  writeUint32LE(hv, off, vp8xPayloadSize); off += 4;
  const hasAlpha = frameChunks.length > 0 && readFourCC(
    new DataView(frameChunks[0].buffer, frameChunks[0].byteOffset, frameChunks[0].byteLength),
    0,
  ) === "ALPH";
  const flags = 0x02 | (hasAlpha ? 0x10 : 0);
  hv.setUint8(off, flags); off += 4;
  writeUint24LE(hv, off, width - 1); off += 3;
  writeUint24LE(hv, off, height - 1); off += 3;

  writeFourCC(hv, off, "ANIM"); off += 4;
  writeUint32LE(hv, off, animPayloadSize); off += 4;
  hv.setUint8(off, 0xf5); off++;
  hv.setUint8(off, 0xf5); off++;
  hv.setUint8(off, 0xf5); off++;
  hv.setUint8(off, 0xff); off++;
  writeUint16LE(hv, off, 0);

  const parts: BlobPart[] = [headerBuf];
  const padByte = new Uint8Array([0]);
  for (let i = 0; i < frameChunks.length; i++) {
    parts.push(anmfHeaders[i]);
    parts.push(frameChunks[i].slice().buffer);
    const anmfPayloadSize = 16 + frameChunks[i].byteLength;
    if (anmfPayloadSize % 2 !== 0) {
      parts.push(padByte);
    }
  }

  return new Blob(parts, { type: "image/webp" });
}

/**
 * 从当前 SpinePreviewApp 导出 Animated WebP；无主 Spine 时仅静态叠层也可导出。
 */
export async function exportWebp({
  app,
  spineCanvas,
  outputSize = 512,
  fps = 50,
  quality = 0.9,
  durationMs,
  maxFrames,
  transparentBackground = true,
  highQualityCapture = true,
  onProgress,
  staticStackItems,
  staticUrlsBelow,
  staticUrlsAbove,
  konvaUnderlay,
  konvaOverlay,
  spineAboveApp,
  spineAboveCanvas,
  spineBelowRender,
}: ExportWebpOptions): Promise<Blob> {
  const useSpine =
    !!app &&
    !!spineCanvas &&
    (app.instances.length > 0 ||
      (spineAboveApp?.instances.length ?? 0) > 0);
  const useStaticOnly =
    !useSpine &&
    !!staticStackItems?.some((i) => i.kind === "img");

  if (!useSpine && !useStaticOnly) {
    throw new Error("没有可渲染的 Spine 或静态图层");
  }

  const { frames } = useSpine
    ? await captureAllFrames({
        app: app!,
        spineCanvas: spineCanvas!,
        outputSize,
        fps,
        durationMs,
        maxFrames,
        onFrameProgress: (p) => onProgress?.(p * 0.4),
        staticStackItems,
        staticUrlsBelow,
        staticUrlsAbove,
        konvaUnderlay,
        konvaOverlay,
        transparentBackground,
        highQualityCapture,
        spineAboveApp,
        spineAboveCanvas,
        spineBelowRender,
      })
    : await captureStaticStackFramesOnly({
        staticStackItems: staticStackItems!,
        outputSize,
        fps,
        durationMs: durationMs ?? 2000,
        maxFrames,
        onFrameProgress: (p) => onProgress?.(p * 0.4),
        transparentBackground,
      });

  const frameChunks: Uint8Array[] = [];
  const frameDurations: number[] = [];

  for (let i = 0; i < frames.length; i++) {
    onProgress?.(0.4 + (i / frames.length) * 0.4);
    const blob = await canvasToBlobAsync(frames[i].canvas, "image/webp", quality);
    const chunk = await extractFrameData(blob);
    frameChunks.push(chunk);
    frameDurations.push(frames[i].frameDelay);
  }

  onProgress?.(0.9);

  const result = muxAnimatedWebp(frameChunks, frameDurations, outputSize, outputSize);

  onProgress?.(1.0);
  return result;
}
